'use client';

import type React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useConsent } from '../../contexts/ConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYouTubeItems } from '../../hooks';

const Section = styled.section`
  min-height: 80vh;
  background: var(--bg-secondary, #080810);
  padding: 10vh 0 12vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 10;
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 8vh 0 9vh;
  }

  @media (max-width: 480px) {
    padding: 7vh 0 8vh;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 6vh;
  padding: 0 16px;
`;

const SectionLabel = styled.p`
  font-size: 0.85rem;
  color: #ff0000;
  font-variant-caps: all-petite-caps;
  letter-spacing: 4px;
  margin: 0 0 8px;
  font-weight: 600;

  @media (max-width: 480px) {
    letter-spacing: 2.5px;
    font-size: 0.78rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3.2rem);
  color: var(--text-primary, #fff);
  font-variant-caps: all-petite-caps;
  margin: 0 0 12px;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: clamp(1.6rem, 8vw, 2.2rem);
    margin: 0 0 10px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary, rgba(255, 255, 255, 0.75));
  font-variant-caps: all-petite-caps;
  margin: 0;
  max-width: 480px;

  @media (max-width: 480px) {
    font-size: 0.86rem;
    max-width: 340px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1100px;
  padding: 0 20px;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const Card = styled.div`
  border-radius: 12px;
  background: var(--card-bg, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--card-border, rgba(255, 255, 255, 0.08));
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 18px var(--card-shadow, rgba(0, 0, 0, 0.2));
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(255, 0, 0, 0.6);
    box-shadow: 0 8px 30px var(--card-shadow, rgba(0, 0, 0, 0.4));
  }
`;

const IframeWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  background: #111;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ConsentPlaceholder = styled.div<{ $thumbnail: string }>`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.85)), url(${({ $thumbnail }) => $thumbnail});
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-image: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.75)), url(${({ $thumbnail }) => $thumbnail});
  }
`;

const PlayIconBadge = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 0, 0, 0.85);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  box-shadow: 0 0 20px rgba(255, 0, 0, 0.5);
  transition: transform 0.2s ease;

  ${ConsentPlaceholder}:hover & {
    transform: scale(1.1);
    background: #ff0000;
  }
`;

const ConsentNotice = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  font-variant-caps: all-petite-caps;
  letter-spacing: 0.5px;
  line-height: 1.3;
  max-width: 240px;
`;

const CardBody = styled.div`
  padding: 16px 20px 20px;

  @media (max-width: 480px) {
    padding: 14px 14px 16px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.05rem;
  color: var(--text-primary, #fff);
  font-variant-caps: all-petite-caps;
  margin: 0 0 8px;
  font-weight: 600;
`;

const CardLink = styled.a`
  font-size: 0.75rem;
  color: #ff4444;
  text-decoration: none;
  font-variant-caps: all-petite-caps;
  letter-spacing: 1px;

  &:hover {
    text-decoration: underline;
  }
`;

/**
 * YouTube videos and live visualizers showcase with 2-click GDPR consent and i18n support.
 */
export const YouTubeSection: React.FC = () => {
  const { items, loading } = useYouTubeItems();
  const { preferences, allowYouTube } = useConsent();
  const { t } = useLanguage();
  const [unlockedVideos, setUnlockedVideos] = useState<Record<string, boolean>>({});

  const handleUnlockSingle = (id: string) => {
    setUnlockedVideos((prev) => ({ ...prev, [id]: true }));
    allowYouTube();
  };

  return (
    <Section id="youtube-videos" aria-label="YouTube Videos and Live Sets">
      <SectionHeader>
        <SectionLabel>{t('ytSectionLabel')}</SectionLabel>
        <SectionTitle>{t('ytSectionTitle')}</SectionTitle>
        <SectionSubtitle>{t('ytSectionSubtitle')}</SectionSubtitle>
      </SectionHeader>

      <Grid>
        {items.slice(0, 6).map((video) => {
          const isAllowed = preferences.youtube || unlockedVideos[video.id];
          const thumbnail = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;

          return (
            <Card key={`yt-${video.id}`}>
              <IframeWrapper>
                {isAllowed ? (
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${video.id}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; unload"
                    allowFullScreen
                    loading="lazy"
                  />
                ) : (
                  <ConsentPlaceholder
                    $thumbnail={thumbnail}
                    onClick={() => handleUnlockSingle(video.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Load and play YouTube video: ${video.title}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleUnlockSingle(video.id);
                    }}
                  >
                    <PlayIconBadge>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="6,4 20,12 6,20" />
                      </svg>
                    </PlayIconBadge>
                    <ConsentNotice>{t('ytConsentNotice')}</ConsentNotice>
                  </ConsentPlaceholder>
                )}
              </IframeWrapper>
              <CardBody>
                <CardTitle>{video.title}</CardTitle>
                <CardLink
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Watch ${video.title} directly on YouTube`}
                >
                  {t('ytCtaButton')}
                </CardLink>
              </CardBody>
            </Card>
          );
        })}
      </Grid>
      {loading && <p style={{ color: 'rgba(255,255,255,0.4)', marginTop: 20 }}>{t('ytLoading')}</p>}
    </Section>
  );
};

export default YouTubeSection;
